/* eslint-disable no-underscore-dangle */
/**
 * HOW MUCH OF AN ORDER LINE HAS BEEN PAID, customer side. The dashboard's
 * ONE implementation of that question: every screen that shows a per-line
 * paid or outstanding figure goes through here.
 *
 * It mirrors `paidByOrderLine` in
 *   pickuphungary-dashboard-api/src/packages/ph-core/src/Helpers/OrderLinePaid.helper.ts
 * and THAT FILE'S HEADER IS THE SPECIFICATION — read it before changing
 * anything here, and change both together, the way `orderLineKey.js` is kept
 * in step with `OrderLineKey.helper.ts`. In summary:
 *
 *  1. A `Payment.lines[]` entry naming a LIVE `order_line_key` pays that line.
 *  2. Everything else the payment carries for the booking — a
 *     `counterparty.kind:'booking'` row with no `lines[]`, a line that names
 *     the booking but no key, and (rule 4) a line whose key the booking no
 *     longer has — is a BOOKING-LEVEL pool, consumed FIFO across `targets`
 *     in the order the caller passes them.
 *  3. A payment in the opposite direction subtracts. A pool that nets
 *     negative is not applied at all, and a line's own total is clamped at
 *     zero: a refund must never push a line's outstanding ABOVE what it owes.
 *  4. Money on a key the booking no longer has drops to the pool only when
 *     the payment's counterparty IS that booking.
 *
 * WHY THIS IS A MIRROR AND NOT AN API FIELD. The honest alternative was to
 * have the API compute per-line paid and have this repo render it, as
 * `BookingsService.enrichBookings` does for `profit_summary` and
 * `operators[].resolved_cost`. Three things decided it the other way:
 *
 *  - `buildPaidByLineKey` (PaymentAllocationRows) cannot be served from a
 *    booking payload at all. It must exclude the payment being re-allocated
 *    (`excludePaymentId`) and it is asked per side; a precomputed field
 *    cannot express "paid, excluding this row". Serving only the Invoicing
 *    panel from the API would have left that reader re-deriving the policy,
 *    which is the bug this ticket is about.
 *  - `enrichBookings` loads no payments today (partners, drivers, operators,
 *    cars, FX) and runs on essentially every bookings response. Per-line paid
 *    there is a `Payment` read per booking per page, for a figure one modal
 *    panel reads.
 *  - Every reader already holds the payments: `PaymentsService.listForBooking`
 *    (API `PaymentService.findByBooking`) returns every payment touching the
 *    booking. The panel refetches it after recording a payment and recomputes
 *    in place; off a booking payload the figures would not move until the
 *    parent list refetched.
 *
 * WHY THE PAYMENTS ARE FILTERED BY SIDE BEFORE THE POLICY RUNS, and why that
 * is not optional. `findByBooking` returns BOTH sides of the ledger —
 * customer money in and operator payouts out. Rule 2 pools any line that
 * names the booking without a key, and operator payout lines are frequently
 * unkeyed, so without this filter an outbound payout SUBTRACTS from the
 * customer-side pool. PU_DASH_1742653 is the shape: a partner settlement wire
 * carries an unkeyed line for the booking, and TWO operator payouts carry
 * unkeyed lines against it — 67,500 HUF and a second of 205.83 HUF, the
 * latter being the EUR figure typed into a HUF payout. Pooled together the
 * base line reads nothing paid on money the partner has sent. The bug does
 * not depend on that coincidence: ANY unkeyed operator line subtracts. A
 * replay of the whole ledger on 2026-11-01 found 838 bookings whose panel
 * figures change depending on this filter alone. `paymentAllocationRefunds`
 * has asserted the same rule since before this module existed ("Customer-side
 * must not absorb an operator payout").
 *
 * ph-core CARRIES THIS RULE TOO, as of 2026-11-02 (`6a1edb36`): `paidByOrderLine`
 * and `collectPaymentCredits` take a REQUIRED `counterpartyKinds` set, and
 * `PaymentsController.outstandingLines` passes `CUSTOMER_COUNTERPARTY_KINDS`
 * or `OPERATOR_COUNTERPARTY_KINDS` by view. This file's constant deliberately
 * uses the same vocabulary. An earlier revision of this note said ph-core did
 * not carry it and that `outstandingLines` was still exposed — both were true
 * when written and are not now. Keep the two in step: the API side measured
 * 834 bookings changing across its three views, which is a different
 * population from the 838 panels above, not a contradiction of it.
 *
 * WHAT IT CHANGES ON SCREEN. Prod replay of the Invoicing panel over every
 * booking with a customer-facing order line and a payment, 2026-11-01: 3,153
 * bookings in scope, 942 panels change, 809 of them read MORE paid per line,
 * 2 read less (both real refunds the old reader ignored because it summed
 * `direction:'in'` only). The "paid but not allocated to a specific line"
 * note drops from 970 bookings to 128 — and on those 128 it now means what it
 * says, money received beyond what the lines owe. 215,518.01 EUR and
 * 1,037,131.80 HUF move off that note and onto a line.
 *
 * WHO USES IT, AND WHO DELIBERATELY DOES NOT. `billingoDocuments.paidByLine`
 * (the Financial → Invoicing panel and the Billingo line picker) and
 * `buildPaidByLineKey` (the Record / Edit / Link payment modals) both read
 * through here. The Order Ledger tab (`Pages/Orders/Components/OrderLedger`)
 * does NOT, on purpose: it is a double-entry view that shows attributed money
 * per line and unattributed money in its own footer row, per side, and that
 * distinction is the point of the screen — pooling would erase it. It also
 * renders the operator column, which this policy does not pool (below). If
 * its partner column is ever moved onto this policy, it needs the footer
 * reworked in the same change, not a quiet switch.
 *
 * WHY A MISSING COUNTERPARTY IS NOT HANDLED. The side filter drops a payment
 * whose `counterparty.kind` is absent or is `system`. Checked rather than
 * assumed (prod, 2026-11-01): 0 of 1,852 payments have no counterparty, and
 * none of the 645 `system` rows — the unmatched-Stripe pool — names a booking
 * in `lines[]` at all. A Stripe row only acquires a booking when it is
 * reassigned, and that rewrites the counterparty to that booking. So this
 * filter loses no money today; if `system` rows ever carry booking lines, it
 * will, and that is the measurement to re-run.
 *
 * THE OPERATOR SIDE IS DELIBERATELY NOT HERE. Pooling unkeyed money across a
 * booking's operator lines is the one thing rule 4 of the spec forbids: on a
 * multi-operator booking it pays the WRONG operator (PU_DASH_5980739, four
 * operators, two wires pooled to 650,200 and swallowed a third operator's own
 * 265,000 line). The operator-side reader stays keyed-lines-only; see
 * `buildPaidByLineKey`.
 *
 * NO FX, exactly as in ph-core: every amount is taken at face value in its
 * own currency. Checked rather than assumed — 0 of the customer-side payment
 * lines in prod name a currency different from the line they pay
 * (2026-11-01). The operator side is where the mixed-currency rows live, and
 * it does not come through here.
 */

import { getOrderLineKeySetForBooking } from './orderLineKey';
import { SETTLED_PAYMENT_STATUSES } from './paymentStatus';

// The counterparties a CUSTOMER-side payment can have: the booking itself
// (card, cash, wire from the traveller) or the partner that sold it. An
// operator is the other side of the ledger — see the header.
export const CUSTOMER_COUNTERPARTY_KINDS = new Set(['booking', 'partner']);

/** A settled payment on the customer side of the ledger. */
export function isCustomerSidePayment(payment) {
  return !!payment
    && SETTLED_PAYMENT_STATUSES.has(payment.status)
    && CUSTOMER_COUNTERPARTY_KINDS.has(payment.counterparty?.kind);
}

function numberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * What has been paid against one booking's order lines, and what is left over.
 *
 * `targets` are `{ key, owed }` in the order the caller wants the pool spent —
 * pass them in `order_lines` order so the money lands the way the ledger reads
 * on screen. `owed` is the line's customer-side debt in its own currency.
 *
 * `byKey` carries an entry for every target (0 when nothing paid), so a caller
 * can render a column without reaching for `|| 0`. `unapplied` is the pool no
 * line could absorb: under this policy that means the booking is OVERPAID (or
 * its ledger shrank under a payment that already landed), which is worth
 * saying out loud — it is no longer the everyday "nobody keyed this payment"
 * case the panel used to report.
 */
export function paidByOrderLine({ booking, payments, targets }) {
  const bookingId = booking?._id ? String(booking._id) : '';
  const liveKeys = getOrderLineKeySetForBooking(booking);
  const byLine = Object.create(null);
  let pool = 0;

  (payments || []).filter(isCustomerSidePayment).forEach((payment) => {
    // Inbound adds, outbound subtracts (rule 3). Both legs of a refund pair
    // are loaded — that is why SETTLED_PAYMENT_STATUSES includes 'refunded',
    // and why this sum must stay signed; see paymentStatus.js.
    const sign = payment.direction === 'in' ? 1 : -1;
    const lines = (payment.lines || []).filter(Boolean);
    const counterpartyBookingId = payment.counterparty.kind === 'booking'
      ? String(payment.counterparty.id ?? '')
      : '';

    if (lines.length === 0) {
      // A booking-counterparty row with no itemisation: the whole gross is
      // this booking's, at booking granularity.
      if (counterpartyBookingId === bookingId && bookingId) {
        pool += sign * numberOrZero(payment.gross_amount);
      }
      return;
    }

    lines.forEach((line) => {
      if (!bookingId || String(line.booking_id ?? '') !== bookingId) return;
      const amount = numberOrZero(line.gross_amount);
      if (amount === 0) return;
      const key = line.order_line_key ? String(line.order_line_key) : '';
      if (!key) {
        // Named the booking but not a line: booking-level by construction.
        pool += sign * amount;
      } else if (liveKeys.has(key) || counterpartyBookingId !== bookingId) {
        // Live, or dead on a wire whose pool order would be a guess (rule 4).
        // A dead key left here is simply never read, which is the
        // conservative outcome: it shows more outstanding than is owed, which
        // an admin can see and fix.
        byLine[key] = (byLine[key] || 0) + sign * amount;
      } else {
        // Dead key on this booking's own payment (rule 4).
        pool += sign * amount;
      }
    });
  });

  const byKey = Object.create(null);
  (targets || []).forEach((target) => {
    const perLine = byLine[target.key] || 0;
    const residual = numberOrZero(target.owed) - perLine;
    const applied = pool > 0 && residual > 0 ? Math.min(pool, residual) : 0;
    pool -= applied;
    byKey[target.key] = Math.max(0, perLine + applied);
  });

  return { byKey, unapplied: Math.max(0, pool) };
}

export default paidByOrderLine;
